import React, { useMemo } from "react";
import ClassNames from "classnames";
import Collapse from "../Collapse/Collapse.js";
import SidebarItem from "./SidebarItem";
import Tooltip from "../Tooltip/Tooltip";
import { getDataAnalyticsProps } from "../../../utils/UserAnalyticsUtils";
import type { SidebarMenuItem } from "./Sidebar";

export interface SidebarCategoryProps {
  category: SidebarMenuItem;
  searchValue: string;
  emptySearchLabel: string;
  activePath?: string;
  "data-testid": string;
}

const SidebarCategory: React.FC<SidebarCategoryProps> = ({
  category,
  searchValue,
  emptySearchLabel,
  activePath,
  "data-testid": dataTestId,
}) => {
  const {
    title,
    items,
    icon,
    collapsable,
    defaultExpanded,
    emptyLabel,
    headerActions,
  } = category;
  const hasHeader = !!title;

  const itemsList = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    const filteredItems = query
      ? items.filter((item) => item.title.toLowerCase().includes(query))
      : items;

    if (!filteredItems.length) {
      const label = searchValue ? emptySearchLabel : emptyLabel;
      return label ? (
        <span className="sidebar-category-empty">{label}</span>
      ) : null;
    }

    return (
      <ul className="sidebar-category-items">
        {filteredItems.map((item, index) => (
          // Keyed Fragment keeps the list key off SidebarItem's own props.
          <React.Fragment key={`${item.title}-${index}`}>
            <SidebarItem
              item={item}
              searchValue={searchValue}
              isActive={item.path === activePath}
              data-testid={dataTestId}
            />
          </React.Fragment>
        ))}
      </ul>
    );
  }, [
    items,
    searchValue,
    activePath,
    dataTestId,
    emptySearchLabel,
    emptyLabel,
  ]);

  return (
    <div
      className={ClassNames("sidebar-category", {
        "sidebar-category--flat": !hasHeader,
      })}
      data-testid={
        hasHeader ? `${dataTestId}-category-${title}` : `${dataTestId}-category`
      }
    >
      {collapsable && hasHeader ? (
        <Collapse
          key={searchValue ? "search" : "default"}
          className="sidebar-category-collapse"
          label={
            <Tooltip content={title} showOnOverflow placement="top">
              <span className="sidebar-category-title">{title}</span>
            </Tooltip>
          }
          initialState={searchValue ? true : (defaultExpanded ?? true)}
          headerEndContent={headerActions}
          {...getDataAnalyticsProps(category)}
        >
          {itemsList}
        </Collapse>
      ) : (
        <>
          {hasHeader && (
            <div className="sidebar-category-header">
              {!!icon && (
                <i className={`seem-icon sidebar-category-glyph ${icon}`} />
              )}
              <Tooltip content={title} showOnOverflow placement="top">
                <span className="sidebar-category-title">{title}</span>
              </Tooltip>
              {!!headerActions && (
                <div className="sidebar-category-header-actions">
                  {headerActions}
                </div>
              )}
            </div>
          )}
          {itemsList}
        </>
      )}
    </div>
  );
};

export default SidebarCategory;
